import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { SearchInput } from "../../../components/ui/search-input";
import { Label } from "../../../components/ui/label";
import { Checkbox } from "../../../components/ui/checkbox";
import { Select, SelectItem, SelectTrigger, SelectValue, SelectLabel } from "../../../components/ui/select";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../config/api";
import { getSpecColor } from "../../../utils/specColors";
import { SelectContent } from "@radix-ui/react-select";
import { Plus, Search, ChevronLeft, ChevronRight, Trash2, Edit2, Package, Upload, Image, Download } from "lucide-react";
import { useDropzone } from "react-dropzone";
import { Loader } from "../../../components/Loader";
import { buildCsvFilename, downloadCsv } from "../../../utils/csvExport";
import { normalizeSearchQuery } from "../../../utils/search";
import { toMediaSrc } from "../../../utils/mediaSrc";
import { AuthMediaImg } from "../../../components/AuthMediaImg";

export const ProductsPage = () => {
  const [products, setProducts] = useState<any[]>([]);
  const [totalProducts, setTotalProducts] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isSpecificationOpen, setIsSpecificationOpen] = useState(false);
  const [editingSpecIndex, setEditingSpecIndex] = useState<number | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [appliedSearch, setAppliedSearch] = useState<string>("");
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [image, setImage] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<any>(null);
const [formData, setFormData] = useState<any>({
  id: "",
  name: "",
  hsnCode: "",
  price: "",
  gst: "",
  specifications: [],
  formulaType: "",
   isCloth: false,
   productImage: "",
   categoryId: "",
});
  const [colors, setColors] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [spec, setSpec] = useState<any>({
    title: "",
    value: [],
    type: "",
  });
  const { authToken = "", id = "" } = useSelector(
    (state: any) => state.loginData || {}
  );
  const [showColorDropdown, setShowColorDropdown] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [currentInput, setCurrentInput] = useState<string>("");

  const maxResults = 10;

  // Dropzone hook
  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/*" as any,
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      setImage(file);
      convertToBase64(file);
    },
  });

  // Convert the image to base64
  const convertToBase64 = (file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setFormData((prevData: typeof formData) => ({
        ...prevData,
        productImage: base64String,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleAddTag = (value: string) => {
    if (value && !tags.includes(value)) {
      setTags([...tags, value]);
      let tagsList = [...tags, value];
      setSpec({ ...spec, value: tagsList });
      setCurrentInput("");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInput(e.target.value);
  };

  const handleRemoveTag = (tag: string) => {
    const updatedTags = tags.filter((t) => t !== tag);
    setTags(updatedTags);
    setSpec({ ...spec, value: updatedTags });
  };

  useEffect(() => {
    fetchColors();
    fetchCategoriesList();
  }, []);

  const fetchColors = async () => {
    try {
      const { fetchColorsCached } = await import("../../../utils/colorsCache");
      const { colors: list } = await fetchColorsCached({
        accountId: id,
        authToken,
        lite: true,
        maxResults: "all",
      });
      setColors(list);
    } catch (error) {
      console.error("Error fetching colors:", error);
    }
  };

  const fetchCategoriesList = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}account/${id}/categories`, {
        params: { firstResults: 0, maxResults: "all" },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setCategories(response.data?.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  };

  const fetchProducts = async (page = 1, overrides?: { maxResults?: number | string; search?: string; categoryId?: string }) => {
    try {
      const limit = overrides?.maxResults ?? maxResults;
      const firstResults =
        typeof limit === "string" || limit === 0 ? 0 : (page - 1) * (limit as number);
      const params: Record<string, unknown> = {
        firstResults,
        maxResults: limit,
        lite: true,
      };
      if (overrides && Object.prototype.hasOwnProperty.call(overrides, "search")) {
        const q = (overrides.search || "").trim();
        if (q) params.search = q;
      }
      const cat =
        overrides && Object.prototype.hasOwnProperty.call(overrides, "categoryId")
          ? overrides.categoryId
          : categoryFilter;
      if (cat && cat !== "all") params.categoryId = cat;
      const response = await axios.get(`${API_BASE_URL}account/${id}/products`, {
        params,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching products:", error);
      return { products: [], totalCount: 0 };
    }
  };

  const matchesProductSearch = (product: any, term: string) => {
    const q = normalizeSearchQuery(term).toLowerCase();
    if (!q) return true;
    return (
      product.name?.toLowerCase().includes(q) ||
      String(product.hsnCode ?? "").toLowerCase().includes(q)
    );
  };

  const handleExportCsv = async () => {
    const term = normalizeSearchQuery(searchTerm);
    setExporting(true);
    try {
      const result = await fetchProducts(1, { maxResults: "all", search: term });
      let exportProducts: any[] = result.products || [];
      // Client-side HSN match as fallback if API search is name-only / unavailable
      exportProducts = exportProducts.filter((p) => matchesProductSearch(p, term));

      if (exportProducts.length === 0) {
        window.alert("No products to export for the current filters.");
        return;
      }

      const headers = [
        "ID",
        "Name",
        "Category",
        "HSN Code",
        "Price",
        "GST %",
        "Formula",
        "Is Cloth",
        "Specifications",
      ];

      const rows = exportProducts.map((product) => [
        product.id,
        product.name,
        product.categoryName || "",
        product.hsnCode,
        product.price,
        product.gst,
        product.formulaType ?? "",
        product.isCloth ? "Yes" : "No",
        Array.isArray(product.specifications)
          ? product.specifications
              .map((s: any) => {
                const val = Array.isArray(s.value) ? s.value.join("; ") : s.value ?? "";
                return `${s.title}: ${val}`;
              })
              .join(" | ")
          : "",
      ]);

      downloadCsv(buildCsvFilename("products"), headers, rows);
    } catch (error) {
      console.error("Error exporting products CSV:", error);
      window.alert("Failed to export products. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.categoryId) {
      window.alert("Please select a product category.");
      return;
    }
    try {
      if (formData.id) {
        await updateProduct(formData.id, formData);
      } else {
        const newProduct = await createProduct(formData);
        setProducts([...products, newProduct]);
      }
      const loadProducts = async () => {
        const result = await fetchProducts(page, { search: appliedSearch });
        setProducts(result.products);
        setTotalProducts(result.totalCount);
      };
      loadProducts();
      setIsDrawerOpen(false);
      resetForm();
    } catch (error: any) {
      const msg = error?.response?.data?.message || "Failed to submit product.";
      window.alert(msg);
      console.error("Failed to submit product:", error);
    }
  };

const createProduct = async (productData: any) => {
  const payload = {
    name: productData.name,
    hsnCode: productData.hsnCode,
    price: productData.price,
    gst: productData.gst,
    specifications: productData.specifications || [],
    formulaType: productData.formulaType,
    isCloth: productData.isCloth,
    productImage: productData.productImage || "",
    categoryId: Number(productData.categoryId),
  };
  const response = await axios.post(
    `${API_BASE_URL}account/${id}/product/create`,
    payload,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return response.data;
};


const updateProduct = async (productId: string, productData: any) => {
  const payload = {
    id: Number(productId),
    name: productData.name,
    hsnCode: productData.hsnCode,
    price: productData.price,
    gst: productData.gst,
    specifications: productData.specifications || [],
    formulaType: productData.formulaType,
    isCloth: productData.isCloth,
    productImage: productData.productImage || "",
    categoryId: Number(productData.categoryId),
  };
  const response = await axios.put(
    `${API_BASE_URL}account/${id}/product/update`,
    payload,
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  return response.data;
};



  const handleDelete = async (productId: string) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteProduct(productId);
        setProducts(products.filter((product) => product.id !== productId));
      } catch (error) {
        console.error("Failed to delete product:", error);
      }
    }
  };

  const deleteProduct = async (productId: string) => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}account/${id}/product/${productId}/delete`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting product:", error);
      throw error;
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      const q = normalizeSearchQuery(searchTerm);
      setAppliedSearch((prev) => {
        if (prev !== q) {
          setPage(1);
        }
        return q;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    const loadProducts = async () => {
      setTableLoading(true);
      try {
        const result = await fetchProducts(page, { search: appliedSearch });
        setProducts(result.products || []);
        setTotalProducts(result.totalCount || 0);
      } finally {
        setInitialLoading(false);
        setTableLoading(false);
      }
    };
    loadProducts();
  }, [page, appliedSearch, categoryFilter]);

const openDrawerForCreate = () => {
  setFormData({
    id: "",
    name: "",
    hsnCode: "",
    price: "",
    gst: "",
    specifications: [],
    formulaType: "",
    isCloth: false,
    productImage: "",
    categoryId: "",
  });
  setIsCreating(true);
  setTags([]);
  setSpec({ title: "", value: [], type: "" });
  setEditingSpecIndex(null);
  setIsSpecificationOpen(false);
  setImage(null);
  setPreviewImage(null);
  setIsDrawerOpen(true);
};

  const openDrawerForEdit = (product: any) => {
    setFormData({
      ...product,
      categoryId: product.categoryId ? String(product.categoryId) : "",
    });
    setIsCreating(false);
    setPreviewImage(product.productImage);
    setTags([]);
    setSpec({ title: "", value: [], type: "" });
    setEditingSpecIndex(null);
    setIsSpecificationOpen(false);
    setIsDrawerOpen(true);
  };

  const handleColorChange = (colorId: string) => {
    setSpec((prevSpec: any) => {
      const newValues = prevSpec.value.includes(colorId)
        ? prevSpec.value.filter((id: string) => id !== colorId)
        : [...prevSpec.value, colorId];
      return { ...prevSpec, value: newValues };
    });
  };

const resetForm = () => {
  setFormData({
    id: "",
    name: "",
    hsnCode: "",
    price: "",
    gst: "",
    specifications: [],
    formulaType: "",
    isCloth: false,
    productImage: "",
    categoryId: "",
  });
  setTags([]);
  setSpec({ title: "", value: [], type: "" });
  setImage(null);
  setPreviewImage(null);
  setEditingSpecIndex(null);
  setIsSpecificationOpen(false);
};

  const resetSpecForm = () => {
    setSpec({ title: "", value: [], type: "" });
    setTags([]);
    setCurrentInput("");
    setShowColorDropdown(false);
    setEditingSpecIndex(null);
    setIsSpecificationOpen(false);
  };

  const handleEditSpecification = (index: number) => {
    const existing = formData.specifications[index];
    const value = Array.isArray(existing.value)
      ? [...existing.value]
      : existing.value
        ? [existing.value]
        : [];
    setSpec({
      title: existing.title || "",
      value,
      type: existing.type || "",
    });
    setTags(existing.type === "dropdown" ? value.map(String) : []);
    setCurrentInput("");
    setShowColorDropdown(false);
    setEditingSpecIndex(index);
    setIsSpecificationOpen(true);
  };

  const handleDeleteSpecification = (index: number) => {
    const updatedSpecifications = formData.specifications.filter(
      (_spec: any, i: number) => i !== index
    );
    setFormData({
      ...formData,
      specifications: updatedSpecifications,
    });
    if (editingSpecIndex === index) {
      resetSpecForm();
    } else if (editingSpecIndex !== null && editingSpecIndex > index) {
      setEditingSpecIndex(editingSpecIndex - 1);
    }
  };

  const filteredProducts = products;
  const totalPages = Math.max(1, Math.ceil((totalProducts || 0) / maxResults));

  if (initialLoading && products.length === 0) {
    return <Loader />;
  }

  return (
    <div className="space-y-6">
      <style>{`
        .products-desktop-scroll {
          overflow-x: scroll;
          overflow-y: auto;
          scrollbar-gutter: stable;
        }
        .products-desktop-scroll::-webkit-scrollbar {
          height: 14px;
          width: 10px;
        }
        .products-desktop-scroll::-webkit-scrollbar-track {
          background: #e5e7eb;
          border-top: 1px solid #d1d5db;
        }
        .products-desktop-scroll::-webkit-scrollbar-thumb {
          background: #6b7280;
          border-radius: 8px;
          border: 2px solid #e5e7eb;
        }
        .products-desktop-scroll::-webkit-scrollbar-thumb:hover {
          background: #4b5563;
        }
      `}</style>
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="text-blue-600" size={32} />
            Products
          </h1>
          <p className="text-gray-500 text-sm mt-1">Manage and organize your product catalog</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            onClick={handleExportCsv}
            disabled={exporting}
            className="flex items-center gap-2 border-gray-300 w-full sm:w-auto disabled:opacity-50"
          >
            <Download size={18} />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
          <Button
            onClick={openDrawerForCreate}
            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 w-full sm:w-auto"
          >
            <Plus size={18} />
            New Product
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 text-gray-400 pointer-events-none" size={20} />
          <SearchInput
            placeholder="Search by product name or HSN code..."
            value={searchTerm}
            onValueChange={setSearchTerm}
            className="pl-10 py-2 border border-gray-300 rounded-lg"
          />
          {tableLoading && searchTerm.trim() ? (
            <span className="absolute right-3 top-2.5 text-xs font-medium text-blue-600">Searching…</span>
          ) : null}
        </div>
        <div className="sm:w-56">
          <select
            value={categoryFilter}
            onChange={(e) => {
              setPage(1);
              setCategoryFilter(e.target.value);
            }}
            className="w-full h-10 px-3 border border-gray-300 rounded-lg bg-white text-sm text-gray-700"
          >
            <option value="all">All categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={String(cat.id)}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Desktop Table View — fixed-height so the horizontal scrollbar stays at the bottom on laptop/desktop */}
      <div className="hidden md:flex flex-col bg-white rounded-lg border border-gray-200 shadow-sm h-[calc(100vh-320px)] min-h-[280px] relative">
        {tableLoading ? (
          <div className="absolute inset-0 z-20 bg-white/60 flex items-center justify-center pointer-events-none">
            <div className="h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : null}
        {filteredProducts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Package className="mx-auto mb-4 text-gray-300" size={48} />
            <p className="font-medium">No products found</p>
          </div>
        ) : (
          <div className="products-desktop-scroll flex-1 min-h-0">
            <table className="w-full min-w-[1100px]">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Image</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Product Name</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Category</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">HSN Code</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Price</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">GST</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Specifications</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
  Formula
</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => (
                  <tr key={product.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm">
                      <AuthMediaImg
                        kind="product"
                        resourceId={product.id}
                        accountId={id}
                        authToken={authToken}
                        inlineSrc={product.productImage}
                        hasImage={Boolean(product.hasImage || product.productImage)}
                        alt={product.name}
                        className="w-12 h-12 rounded border border-gray-300 object-cover cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() =>
                          setPreviewImage(
                            toMediaSrc(product.productImage) ||
                              String(product.id || "")
                          )
                        }
                        fallback={
                          <div className="w-12 h-12 rounded border border-gray-300 bg-gray-200 flex items-center justify-center">
                            <Image className="text-gray-400" size={20} />
                          </div>
                        }
                      />
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{product.name || "-"}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{product.categoryName || "-"}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono">{product.hsnCode || "-"}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-semibold">₹{product.price || "-"}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                        {product.gst || "-"}%
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {product.specifications && product.specifications.length > 0 ? (
                        <div className="space-y-1">
                          {product.specifications.slice(0, 2).map((spec: any, index: number) => (
                            <div key={index} className="text-xs bg-gray-100 px-2 py-1 rounded">
                              <strong>{spec.title}:</strong>{" "}
                              {Array.isArray(spec.value)
                                ? spec.value.join(", ")
                                : spec.value ?? "-"}
                            </div>
                          ))}
                          {product.specifications.length > 2 && (
                            <div className="text-xs text-gray-500">+{product.specifications.length - 2} more</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
  {product.formulaType || "-"}
</td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDrawerForEdit(product)}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                      >
                        <Edit2 size={14} className="mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(product.id)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 size={14} className="mr-1" />
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4 relative">
        {tableLoading ? (
          <div className="absolute inset-0 z-20 bg-white/60 flex items-center justify-center pointer-events-none min-h-[120px]">
            <div className="h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : null}
        {filteredProducts.length === 0 ? (
          <div className="p-8 text-center text-gray-500 bg-white rounded-lg border border-gray-200">
            <Package className="mx-auto mb-4 text-gray-300" size={48} />
            <p className="font-medium">No products found</p>
          </div>
        ) : (
          filteredProducts.map((product) => (
            <div
              key={product.id}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 text-lg">{product.name}</p>
                  <p className="text-xs text-gray-500 font-mono mt-1">HSN: {product.hsnCode}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Category: {product.categoryName || "-"}
                  </p>
                </div>
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                  {product.gst}%
                </span>
              </div>

              {/* Product Image */}
              {(product.hasImage || product.productImage) && (
                <div className="mb-4">
                  <AuthMediaImg
                    kind="product"
                    resourceId={product.id}
                    accountId={id}
                    authToken={authToken}
                    inlineSrc={product.productImage}
                    hasImage={Boolean(product.hasImage || product.productImage)}
                    alt={product.name}
                    className="w-full h-40 object-cover rounded-lg border border-gray-300 cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() =>
                      setPreviewImage(toMediaSrc(product.productImage) || String(product.id))
                    }
                    fallback={null}
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Price</p>
                  <p className="text-lg font-bold text-gray-900">₹{product.price}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">GST Rate</p>
                  <p className="text-lg font-bold text-gray-900">{product.gst}%</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-4">
  <input
    type="checkbox"
    id="isCloth"
    checked={formData.isCloth}
    onChange={(e) =>
      setFormData({ ...formData, isCloth: e.target.checked })
    }
    className="accent-blue-600"
  />
  <label htmlFor="isCloth" className="text-gray-700 text-sm">
    This product is cloth
  </label>
</div>


              {product.specifications && product.specifications.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-600 mb-2">Specifications:</p>
                  <div className="space-y-1">
                    {product.specifications.map((spec: any, index: number) => (
                      <div key={index} className="text-xs bg-blue-50 px-2 py-1 rounded border border-blue-100">
                        <strong>{spec.title}:</strong>{" "}
                        {Array.isArray(spec.value)
                          ? spec.value.join(", ")
                          : spec.value ?? "-"}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => openDrawerForEdit(product)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Edit2 size={14} className="mr-1" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(product.id)}
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 size={14} className="mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-white rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">
            Page <strong>{page}</strong> of <strong>{totalPages}</strong> ({<strong>{totalProducts}</strong>} total products)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="flex items-center gap-2 disabled:opacity-50"
            >
              <ChevronLeft size={18} />
              <span className="hidden sm:inline">Previous</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="flex items-center gap-2 disabled:opacity-50"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      )}

      {/* Drawer Form */}
      {isDrawerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setIsDrawerOpen(false)} />
      )}
      <div
        className={`fixed md:left-1/2 md:top-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 bottom-0 left-0 right-0 md:bottom-auto bg-white z-50 transform transition-all duration-300 w-full md:w-96 md:h-auto max-h-screen md:max-h-[90vh] overflow-y-auto rounded-t-2xl md:rounded-lg ${
          isDrawerOpen ? "translate-y-0 md:scale-100" : "translate-y-full md:scale-0 md:opacity-0 pointer-events-none"
        }`}
      >
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white flex justify-between items-center rounded-t-2xl md:rounded-t-lg">
          <h2 className="text-xl font-bold text-gray-900">
            {isCreating ? "Create Product" : "Edit Product"}
          </h2>
          <button
            onClick={() => setIsDrawerOpen(false)}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 pb-20 md:pb-6">
          <div>
            <Label className="text-gray-700 block mb-2">Product Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter product name"
              required
            />
          </div>

          <div>
            <Label className="text-gray-700 block mb-2">Category *</Label>
            <select
              value={formData.categoryId || ""}
              onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
              className="w-full h-10 px-3 border border-gray-300 rounded-lg bg-white text-sm"
              required
            >
              <option value="" disabled>
                Select category
              </option>
              {categories.map((cat) => (
                <option key={cat.id} value={String(cat.id)}>
                  {cat.name}
                </option>
              ))}
            </select>
            {categories.length === 0 ? (
              <p className="text-xs text-amber-600 mt-1">
                No categories yet. Create one under Product Categories first.
              </p>
            ) : null}
          </div>

          <div>
            <Label className="text-gray-700 block mb-2">HSN Code *</Label>
            <Input
              value={formData.hsnCode}
              onChange={(e) => setFormData({ ...formData, hsnCode: e.target.value })}
              placeholder="Enter HSN code"
              required
            />
          </div>

          {/* Product Image Upload */}
          <div>
            <Label className="text-gray-700 block mb-2">Product Image</Label>
            <div
              {...getRootProps()}
              className="border-2 border-dashed border-blue-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 transition-colors bg-blue-50"
            >
              <input {...getInputProps()} />
              <Upload size={32} className="mx-auto text-blue-500 mb-2" />
              <p className="text-sm text-gray-700 font-medium">Drag and drop your image here</p>
              <p className="text-xs text-gray-500">or click to select</p>
            </div>
            
            {/* Image Preview */}
            {(formData.productImage || (!isCreating && formData.id && formData.hasImage)) && (
              <div className="mt-4 relative">
                {formData.productImage ? (
                  <img
                    src={toMediaSrc(formData.productImage) || formData.productImage}
                    alt="Product Preview"
                    className="w-full h-40 object-cover rounded-lg border border-gray-300 cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => setPreviewImage(formData.productImage)}
                  />
                ) : (
                  <AuthMediaImg
                    kind="product"
                    resourceId={formData.id}
                    accountId={id}
                    authToken={authToken}
                    hasImage
                    alt="Product Preview"
                    className="w-full h-40 object-cover rounded-lg border border-gray-300"
                    fallback={
                      <div className="w-full h-40 rounded-lg border border-gray-300 bg-gray-100 flex items-center justify-center text-sm text-gray-500">
                        No image
                      </div>
                    }
                  />
                )}
                <button
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, productImage: "", hasImage: false });
                    setImage(null);
                  }}
                  className="mt-2 text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Remove Image
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-gray-700 block mb-2">Price *</Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                placeholder="₹"
                required
              />
            </div>
            <div>
              <Label className="text-gray-700 block mb-2">GST % *</Label>
              <Input
                type="number"
                value={formData.gst}
                onChange={(e) => setFormData({ ...formData, gst: e.target.value })}
                placeholder="18%"
                required
              />
            </div>
          </div>
         


          {/* Specifications Section */}
          <div className="border-t pt-4">
            <div className="flex justify-between items-center mb-4">
              <Label className="text-gray-700 font-semibold">Specifications</Label>
              {!isSpecificationOpen && (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    setEditingSpecIndex(null);
                    setSpec({ title: "", value: [], type: "" });
                    setTags([]);
                    setCurrentInput("");
                    setShowColorDropdown(false);
                    setIsSpecificationOpen(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Plus size={14} className="mr-1" />
                  Add
                </Button>
              )}
            </div>

            {isSpecificationOpen && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 space-y-3">
                <div>
                  <Label className="text-sm text-gray-700 block mb-2">Title *</Label>
                  <div className="flex items-center gap-2">
                    {spec.title.trim() ? (
                      <span
                        className="w-8 h-8 rounded-md border border-gray-300 shrink-0"
                        style={{ backgroundColor: getSpecColor(spec.title) }}
                        title={getSpecColor(spec.title)}
                      />
                    ) : null}
                    <Input
                      value={spec.title}
                      onChange={(e) => setSpec({ ...spec, title: e.target.value })}
                      placeholder="e.g., Size, Color, Material"
                      required
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm text-gray-700 block mb-2">Type *</Label>
                  <select
                    value={spec.type}
                    onChange={(e) => {
                      const type = e.target.value;
                      if (type === "place") {
                        setSpec({
                          title: "Place",
                          type: "place",
                          value: ["room", "hall", "kitchen"],
                        });
                      } else {
                        setSpec({ ...spec, type, value: [] });
                        setTags([]);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Type</option>
                    <option value="input">Input</option>
                    <option value="dropdown">Dropdown</option>
                    <option value="color">Color</option>
                    <option value="place">Place</option>
                  </select>
                </div>

                {(spec.type === "dropdown" || spec.type === "place") && (
                  <div>
                    <Label className="text-sm text-gray-700 block mb-2">Values *</Label>
                    {spec.type === "place" && (
                      <div className="flex gap-2 flex-wrap mb-2">
                        {["room", "hall", "kitchen"].map((placeOption) => {
                          const selected = (spec.value || []).includes(placeOption);
                          return (
                            <button
                              key={placeOption}
                              type="button"
                              onClick={() => {
                                const current = Array.isArray(spec.value) ? spec.value : [];
                                const newValues = selected
                                  ? current.filter((v: string) => v !== placeOption)
                                  : [...current, placeOption];
                                setSpec({ ...spec, title: spec.title || "Place", value: newValues });
                              }}
                              className={`px-3 py-1.5 rounded-full text-sm border ${
                                selected
                                  ? "bg-blue-600 text-white border-blue-600"
                                  : "bg-white text-gray-700 border-gray-300"
                              }`}
                            >
                              {placeOption.charAt(0).toUpperCase() + placeOption.slice(1)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {spec.type === "dropdown" && (
                      <>
                    <Input
                      value={currentInput}
                      onChange={handleInputChange}
                      onBlur={() => handleAddTag(currentInput)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddTag(currentInput);
                        }
                      }}
                      placeholder="Type and press Enter"
                    />
                    {tags.length > 0 && (
                      <div className="flex gap-2 flex-wrap mt-2">
                        {tags.map((tag) => (
                          <span key={tag} className="px-3 py-1 bg-white border border-blue-300 rounded-full text-sm flex items-center gap-1">
                            {tag}
                            <button
                              type="button"
                              onClick={() => handleRemoveTag(tag)}
                              className="text-red-500 font-bold hover:text-red-700"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                      </>
                    )}
                  </div>
                )}

                {spec.type === "color" && (
                  <div className="relative">
                    <Label className="text-sm text-gray-700 block mb-2">Select Colors *</Label>
                    
                    {/* Color Dropdown Button */}
                    <button
                      type="button"
                      onClick={() => setShowColorDropdown(!showColorDropdown)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <span className="text-sm text-gray-700">
                        {spec.value.length > 0 ? `${spec.value.length} color${spec.value.length > 1 ? 's' : ''} selected` : 'Select colors...'}
                      </span>
                      <span className="text-gray-500">{showColorDropdown ? '▲' : '▼'}</span>
                    </button>

                    {/* Color Dropdown Menu */}
                    {showColorDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                        {colors.map((color) => (
                          <div
                            key={color.id}
                            onClick={() => {
                              const newValues = spec.value.includes(color.name)
                                ? spec.value.filter((v: string) => v !== color.name)
                                : [...spec.value, color.name];
                              setSpec({ ...spec, value: newValues });
                            }}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer flex items-center gap-3 border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            {/* Color Image */}
                            {color.colorImage && (
                              <img
                                src={color.colorImage}
                                alt={color.name}
                                className="w-8 h-8 rounded border border-gray-300 object-cover"
                              />
                            )}
                            {!color.colorImage && (
                              <div className="w-8 h-8 rounded border border-gray-300 bg-gray-200" />
                            )}
                            
                            {/* Color Name and Checkbox */}
                            <div className="flex-1 flex items-center justify-between">
                              <span className="text-sm text-gray-900 font-medium">{color.name}</span>
                              <div
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                  spec.value.includes(color.name)
                                    ? 'bg-blue-600 border-blue-600'
                                    : 'border-gray-300'
                                }`}
                              >
                                {spec.value.includes(color.name) && (
                                  <span className="text-white text-xs">✓</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Selected Colors Display */}
                    {spec.value.length > 0 && (
                      <div className="flex gap-2 flex-wrap mt-3">
                        {spec.value.map((colorName: string) => {
                          const color = colors.find((c) => c.name === colorName);
                          return (
                            <div
                              key={colorName}
                              className="px-3 py-1.5 bg-blue-100 border border-blue-300 rounded-full flex items-center gap-2 text-sm"
                            >
                              {color?.colorImage && (
                                <img
                                  src={color.colorImage}
                                  alt={colorName}
                                  className="w-5 h-5 rounded border border-blue-400 object-cover"
                                />
                              )}
                              <span className="text-blue-900 font-medium">{colorName}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const newValues = spec.value.filter((v: string) => v !== colorName);
                                  setSpec({ ...spec, value: newValues });
                                }}
                                className="text-blue-600 hover:text-blue-800 font-bold ml-1"
                              >
                                ×
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={resetSpecForm}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      const titleNorm = String(spec.title || "").trim().toLowerCase();
                      if (!titleNorm) return;
                      const duplicate = formData.specifications.some(
                        (s: { title?: string }, idx: number) =>
                          String(s.title || "").trim().toLowerCase() === titleNorm &&
                          idx !== editingSpecIndex
                      );
                      if (duplicate) {
                        window.alert(
                          `A spec named "${spec.title}" already exists. Use a unique name for each spec.`
                        );
                        return;
                      }
                      if (editingSpecIndex !== null) {
                        const updatedSpecifications = [...formData.specifications];
                        updatedSpecifications[editingSpecIndex] = {
                          ...spec,
                          color: spec.color || getSpecColor(spec.title),
                        };
                        setFormData({
                          ...formData,
                          specifications: updatedSpecifications,
                        });
                      } else {
                        setFormData({
                          ...formData,
                          specifications: [
                            ...formData.specifications,
                            { ...spec, color: getSpecColor(spec.title) },
                          ],
                        });
                      }
                      resetSpecForm();
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {editingSpecIndex !== null ? "Update Spec" : "Add Spec"}
                  </Button>
                </div>
              </div>
            )}

            {formData.specifications.length > 0 && (
              <div className="space-y-2">
                {formData.specifications.map((specItem: any, index: number) => {
                  const specTint = specItem.color || getSpecColor(specItem.title);
                  return (
                  <div
                    key={index}
                    className={`bg-gray-50 border rounded-lg p-3 flex justify-between items-start ${
                      editingSpecIndex === index
                        ? "border-blue-400 ring-1 ring-blue-200"
                        : "border-gray-200"
                    }`}
                    style={{ borderLeftWidth: 4, borderLeftColor: specTint }}
                  >
                    <div className="flex-1 flex gap-2.5 items-start min-w-0">
                      <span
                        className="w-5 h-5 rounded-md border border-gray-300 shrink-0 mt-0.5 shadow-sm"
                        style={{ backgroundColor: specTint }}
                        title={`Spec color ${specTint}`}
                        aria-hidden
                      />
                      <div className="min-w-0">
                      <p className="font-semibold text-sm" style={{ color: specTint }}>
                        {specItem.title}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {specItem.value && Array.isArray(specItem.value) ? specItem.value.join(", ") : specItem.value}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Type: {specItem.type}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditSpecification(index)}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        title="Edit specification"
                      >
                        <Edit2 size={14} />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteSpecification(index)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        title="Delete specification"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Formula Choice Section */}
<div className="border-t pt-4">
  <Label className="text-gray-700 font-semibold block mb-3">
    Formula Choice *
  </Label>

  <div className="space-y-2">
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="radio"
        name="formulaType"
        value="width_height"
        checked={formData.formulaType === "width_height"}
        onChange={(e) =>
          setFormData({ ...formData, formulaType: e.target.value })
        }
        className="accent-blue-600"
        required
      />
      <span className="text-sm text-gray-700">Width × Height</span>
    </label>

    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="radio"
        name="formulaType"
        value="length"
        checked={formData.formulaType === "length"}
        onChange={(e) =>
          setFormData({ ...formData, formulaType: e.target.value })
        }
        className="accent-blue-600"
      />
      <span className="text-sm text-gray-700">Length</span>
    </label>

    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="radio"
        name="formulaType"
        value="pieces"
        checked={formData.formulaType === "pieces"}
        onChange={(e) =>
          setFormData({ ...formData, formulaType: e.target.value })
        }
        className="accent-blue-600"
      />
      <span className="text-sm text-gray-700">Pieces</span>
    </label>
  </div>
</div>
{formData.formulaType === "width_height" && 
<div className="flex items-center gap-2 mb-4">
  <input
    type="checkbox"
    id="isCloth"
    checked={formData.isCloth}
    onChange={(e) => setFormData({ ...formData, isCloth: e.target.checked })}
    className="accent-blue-600"
  />
  <label htmlFor="isCloth" className="text-gray-700 text-sm">
    This product is cloth
  </label>
</div>}


          <div className="flex gap-2 pt-4 border-t md:sticky md:bottom-0 md:bg-white">
            <Button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isCreating ? "Create Product" : "Update Product"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDrawerOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[70] flex items-center justify-center p-4" onClick={() => setPreviewImage(null)}>
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in zoom-in-95 duration-300 flex flex-col items-center gap-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Icon */}
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 font-bold text-lg transition-all hover:scale-110"
              title="Close"
            >
              ✕
            </button>

            {/* Enlarged Image */}
            <div className="w-full aspect-square rounded-xl overflow-hidden border-4 border-blue-300 shadow-md flex items-center justify-center bg-gray-50">
              {previewImage ? (
                <img
                  src={previewImage}
                  alt="Product"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                  <span className="text-gray-500 text-sm">No image available</span>
                </div>
              )}
            </div>

            {/* Product Name */}
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-800">{formData.name || "Product Image"}</h3>
              <p className="text-sm text-gray-500 mt-1">Product</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;
